// plugins/router.ts
import { ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import { devLog, devError } from '~/layers/base/utils/logger'

// Extend Window interface to include our custom properties
declare global {
  interface Window {
    __INITIAL_QUERY__?: Record<string, any>;
    __FILTERS_EXPLICITLY_CLEARED__?: boolean;
    __isSynchronizing?: boolean;
    __RESTORE_ATTEMPTS__?: number;
    __QUICKVIEW_STATE__?: {
      isOpen: boolean;
      vehicleId: string;
      scrollPosition: number;
    };
    __FILTER_STATE__?: Record<string, any>;
    __PENDING_URL_RESTORE__?: boolean;
    __PREVIOUS_VEHICLES_QUERY__?: Record<string, any>;
  }
}

/**
 * Router plugin that handles query parameter preservation and navigation
 */
export default defineNuxtPlugin((nuxtApp) => {
  // Only run in client context
  if (!process.client) return {};
  
  // Safely access route and router
  let route: any, router: any;
  
  try {
    // Try to get route and router from nuxtApp
    router = nuxtApp.$router;
    if (router && router.currentRoute && router.currentRoute.value) {
      route = router.currentRoute.value;
      
      // Add fullPath property if it doesn't exist
      if (!route.fullPath && typeof route.path === 'string') {
        Object.defineProperty(route, 'fullPath', {
          get: function() {
            const query = this.query || {};
            const queryString = Object.keys(query).length 
              ? '?' + new URLSearchParams(query as Record<string, string>).toString() 
              : '';
            return this.path + queryString;
          }
        });
      }
    } else {
      devError('[Router Plugin] Router or route not available');
      return {};
    }
  } catch (error) {
    devError('[Router Plugin] Failed to access route/router:', error);
    return {};
  }
  
  // Track if we're handling the initial page load
  let isInitialPageLoad = true;
  
  // Add URL state tracking
  let initialQuery: Record<string, any> | null = null;
  
  // Log initial query parameters
  if (process.client) {
    try {
      // Store initial query parameters
      if (route.path === '/vehicles' && Object.keys(route.query).length > 0) {
        // Filter out quickView parameter from initial query
        const { quickView, ...otherParams } = route.query;
        window.__INITIAL_QUERY__ = { ...otherParams };
        
        // Store filter state
        const filterParams = Object.entries(otherParams).reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {} as Record<string, any>);
        
        if (Object.keys(filterParams).length > 0) {
          window.__FILTER_STATE__ = filterParams;
        }
        
        // Store quickView state if present, but don't auto-launch
        if (quickView) {
          window.__QUICKVIEW_STATE__ = {
            isOpen: false, // Don't auto-launch
            vehicleId: quickView as string,
            scrollPosition: window.scrollY
          };
        }
      }
    } catch (error) {
      devError('[Router Plugin] Error storing initial query:', error);
    }
  }
  
  // Store initial query parameters when the route path is /vehicles
  if (route.path === '/vehicles') {
    try {
      // Filter out quickView parameter from initial query
      const { quickView, ...otherParams } = route.query;
      initialQuery = { ...otherParams };
    } catch (error) {
      devError('[Router Plugin] Error storing initial query parameters:', error);
    }
  }
  
  // Handle route changes
  router.beforeEach((to: any, from: any, next: any) => {
    try {
      // Store previous vehicles page query whenever we navigate from /vehicles to anywhere else
      if (from.path === '/vehicles' && Object.keys(from.query).length > 0) {
        // Store the query parameters excluding quickView
        const { quickView, ...filterParams } = from.query;
        
        // Check if perweek_min and perweek_max parameters are likely from the home page budget slider
        // and remove them if they are
        const cleanFilterParams = { ...filterParams };
        
        if (cleanFilterParams.condition && (cleanFilterParams.perweek_min || cleanFilterParams.perweek_max)) {
          const min = parseInt(cleanFilterParams.perweek_min as string, 10);
          const max = parseInt(cleanFilterParams.perweek_max as string, 10);
          
          // If values look like they're from home page budget slider (typically 100-300 range for min, 200-3000 for max)
          // and we have a condition filter (indicating we're on the vehicles listing page), remove them
          if (min >= 100 && min <= 300 && max >= 200 && max <= 3000) {
            delete cleanFilterParams.perweek_min;
            delete cleanFilterParams.perweek_max;
            devLog('[Router Plugin] Removed likely home page perweek parameters from stored query');
          }
        }
        
        if (Object.keys(cleanFilterParams).length > 0) {
          window.__PREVIOUS_VEHICLES_QUERY__ = { ...cleanFilterParams };
          devLog('[Router Plugin] Stored previous vehicles query:', window.__PREVIOUS_VEHICLES_QUERY__);
        }
      }
      
      // If navigating FROM a vehicle detail page TO the vehicles page via back button
      if (from.path.startsWith('/vehicle-for-sale/') && to.path === '/vehicles' && 
          (!to.query || Object.keys(to.query).length === 0) && 
          window.__PREVIOUS_VEHICLES_QUERY__ && 
          Object.keys(window.__PREVIOUS_VEHICLES_QUERY__).length > 0) {
        
        // Restore previous vehicles page query parameters
        devLog('[Router Plugin] Restoring previous vehicles query on back navigation');
        to.query = { ...window.__PREVIOUS_VEHICLES_QUERY__ };
      }
      
      // Store initial query parameters when navigating to /vehicles
      if (to.path === '/vehicles') {
        // Filter out quickView parameter from initial query
        const { quickView, ...otherParams } = to.query;
        initialQuery = { ...otherParams };
      }
      
      // If we have initial query parameters and we're navigating to /vehicles
      if (initialQuery && to.path === '/vehicles' && process.client) {
        // Skip if we're in the middle of a filter sync operation
        if (window.__isSynchronizing) {
          next();
          return;
        }
        
        // Skip if filters were explicitly cleared
        if (window.__FILTERS_EXPLICITLY_CLEARED__) {
          next();
          return;
        }
        
        // Skip if we're restoring URL state
        if (window.__PENDING_URL_RESTORE__) {
          next();
          return;
        }
        
        // Mark that we're restoring URL state
        window.__PENDING_URL_RESTORE__ = true;
        
        // Update the route with initial query parameters
        to.query = { ...initialQuery };
      }
    } catch (error) {
      devError('[Router Plugin] Error in beforeEach hook:', error);
    }
    
    if (to.path === '/vehicles' && process.client) {
      try {
        // Store filter state without quickView parameter
        const { quickView, ...filterParams } = to.query;
        if (Object.keys(filterParams).length > 0) {
          window.__FILTER_STATE__ = filterParams;
        }
        
        // Store quickView state if present, but don't auto-launch
        if (quickView) {
          window.__QUICKVIEW_STATE__ = {
            isOpen: false, // Don't auto-launch
            vehicleId: quickView as string,
            scrollPosition: window.scrollY
          };
        }
      } catch (error) {
        devError('[Router Plugin] Error storing filter state:', error);
      }
    }
    next();
  });
  
  return {
    provide: {
      isInitialPageLoad: () => isInitialPageLoad,
      setInitialPageLoad: (value: boolean) => {
        isInitialPageLoad = value;
      }
    }
  };
});
